import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { FileText } from "lucide-react";

interface PropertyDescriptionSectionProps {
  description: string;
}

const TRUNCATION_LENGTH = 300;

const PropertyDescriptionSection = ({
  description,
}: PropertyDescriptionSectionProps) => {
  const needsTruncation = description && description.length > TRUNCATION_LENGTH;
  const truncatedDescription = needsTruncation
    ? `${description.substring(0, TRUNCATION_LENGTH)}...`
    : description;

  return (
    <Card className="overflow-hidden border border-propertylk-blue/10">
      <CardHeader className="bg-gradient-to-r from-propertylk-lightblue to-white border-b border-propertylk-blue/10">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-full bg-propertylk-lightblue text-propertylk-blue">
            <FileText className="w-4 h-4" />
          </div>
          <CardTitle className="text-propertylk-darkblue">
            Property Description
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="p-4">
        <div className="prose max-w-none text-gray-700 whitespace-pre-line">
          {description ? (
            <div dangerouslySetInnerHTML={{ __html: truncatedDescription }} />
          ) : (
            <p className="text-gray-500 italic">
              No description available for this property.
            </p>
          )}
        </div>
        {needsTruncation && (
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="mt-4">
                See full description
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[625px]">
              <DialogHeader>
                <DialogTitle>Property Description</DialogTitle>
                <DialogDescription>
                  Full details about the property.
                </DialogDescription>
              </DialogHeader>
              <div
                className="prose max-w-none py-4 text-gray-700 whitespace-pre-line overflow-y-auto max-h-[60vh]"
                dangerouslySetInnerHTML={{ __html: description }}
              />
              <DialogFooter>
                <DialogClose asChild>
                  <Button type="button" variant="secondary">
                    Close
                  </Button>
                </DialogClose>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </CardContent>
    </Card>
  );
};

export default PropertyDescriptionSection;
